/**
 * Pick an audiooutput deviceId that is likely the loudspeaker (not earpiece).
 * Android Chrome often defaults WebRTC remote audio to the earpiece until setSinkId uses a speaker sink.
 *
 * @param {MediaDeviceInfo[]} outputs - devices with kind === 'audiooutput'
 * @param {{ mobileHeuristic?: boolean }} [opts] - on coarse pointer / phone, unlabeled multi-sink order is often earpiece then speaker
 * @returns {string} deviceId or '' if unknown (caller keeps browser default)
 */
export function pickSpeakerSinkId(outputs, opts = {}) {
  const { mobileHeuristic = false } = opts;
  if (!outputs?.length) return "";

  const label = (d) => String(d.label || "").toLowerCase();

  const score = (d) => {
    const l = label(d);
    if (/earpiece|receiver|听筒|ear\s*piece|built-?in\s*recv/i.test(l)) return -100;
    if (/phone\s*call|in-?call|communication|telephony|voip\s*recv/i.test(l)) return -80;
    if (/speaker|loud|spkr|speakerphone|外放|扬声器|扩音|主扬声器|built-?in\s*sp/i.test(l))
      return 100;
    if (/hdmi|displayport|usb.*audio|headphone|headset|airpods|buds|bluetooth|wireless/i.test(l))
      return 20;
    return 0;
  };

  let bestId = "";
  let bestScore = -999;
  for (const d of outputs) {
    if (!d.deviceId) continue;
    const s = score(d);
    if (s > bestScore) {
      bestScore = s;
      bestId = d.deviceId;
    }
  }
  if (bestScore >= 50) return bestId;

  const withIds = outputs.filter((d) => d.deviceId && d.deviceId !== "default");
  if (mobileHeuristic && withIds.length >= 2) {
    const allBlank = withIds.every((d) => !String(d.label || "").trim());
    if (allBlank) return withIds[withIds.length - 1].deviceId;
  }

  return "";
}
